/* ============================================================
   TC60 · v3 — scroll tunnel, rolling headline, spotlight cards,
   3D timeline rail.

   Pairs with tc60.js (reveal / count-up / sticky bar / mock form)
   and tc60-v2.js (voices marquee / bundle calculator).

   Perf contract:
     · every scroll-driven write happens inside one rAF
     · geometry is read in a batch, then written in a batch —
       never interleaved, so we don't thrash layout
     · blur is quantised to 1px steps and cached; re-rasterising
       16 elements per frame is the one thing that would kill this
     · the tunnel loop is parked entirely when the hero is offscreen
   ============================================================ */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var clamp = function (v, a, b) { return v < a ? a : v > b ? b : v; };

  /* ----------------------------------------------------------
     REAL TC PHOTOGRAPHY, not stock.
     Everything below is a live file on the tc-acoustic-asia Shopify CDN,
     pulled from the theme itself — the stores (Adelphi flagship, the Sonos
     concept store at Wheelock, the Experience Centre, the SFS shop), the
     "Everyone's Favourite Company" wall in our own space, the team trip,
     the in-store programmes, the Lutron smart-home rooms and the brand
     lifestyle library.

     Every Unsplash reference is gone from this page. The one thing to know
     when swapping these: they are wide marketing crops, not portraits, so
     the captions describe a PLACE or a MOMENT rather than naming a face.
     When real portrait photography of staff and customers exists, the
     shape of these lists doesn't change — only the file and the caption.
     ---------------------------------------------------------- */
  var CDN = 'https://cdn.shopify.com/s/files/1/0299/5830/7976/files/';
  var img = function (file, w) {
    return CDN + file + '?width=' + w;
  };

  /* the tunnel's stream of tiles: [file, caption]
     Replaced 2026-08-13 with the eleven files Bryan gave directly. Most
     are named after the place in the filename itself; `scs_1.png` and the
     two camera-code files (DSC08733, EDR02504, PZY0671*) don't name a
     place or moment, so those keep generic captions rather than guessing. */
  var PEOPLE = [
    ['scs_1.png',                      'In-store · Together'],
    ['emsphere_1.png',                 'EmSphere'],
    ['TC_20Old_20Office.jpg',          'Our old office'],
    ['Wheelock.jpg',                   'Wheelock Place'],
    ['TC_20New_20Office.jpg',          'Our new office'],
    ['Emsphere.jpg',                   'EmSphere'],
    ['Sonos_Play_Event_Photos_15.jpg', 'Sonos · Play launch'],
    ['DSC08733.jpg',                   'In-store · Together'],
    ['EDR02504.jpg',                   'A demo, in person'],
    ['PZY06717.jpg',                   'Customer · At home'],
    ['PZY06730.jpg',                   'Customer · At home']
  ];

  /* ----------------------------------------------------------
     THE WALL, ROW BY ROW.
     REPLACED WHOLESALE 2026-08-13: the eighteen named/roled/quoted tiles
     above are gone, along with the statement tiles ("60 Years", "4
     Countries", "1966") that used to sit among them — Bryan supplied 18
     plain photographs with no caption data, and asked for the hover
     name/role/quote reveal to go entirely (see 5c below), so there is
     nothing left for a caption to attach to. Split into two rows of nine
     in the order given; row 1 takes the first nine, row 2 the rest. -----
     ---------------------------------------------------------- */
  var WALL_URLS = [
    'https://cdn.shopify.com/s/files/1/0299/5830/7976/files/1_Ace_Launch_MY_2.jpg?v=1786599388',
    'https://cdn.shopify.com/s/files/1/0299/5830/7976/files/1_TC_Emsphere.jpg?v=1786599388',
    'https://cdn.shopify.com/s/files/1/0299/5830/7976/files/2_Ace_Launch_MY_3.jpg?v=1786599388',
    'https://cdn.shopify.com/s/files/1/0299/5830/7976/files/1_Sonos_Popup_MY2.jpg?v=1786599388',
    'https://cdn.shopify.com/s/files/1/0299/5830/7976/files/2_Ace_Launch_MY.jpg?v=1786599443',
    'https://cdn.shopify.com/s/files/1/0299/5830/7976/files/2_Sonos_Popup_MY.jpg?v=1786599444',
    'https://cdn.shopify.com/s/files/1/0299/5830/7976/files/2_Sonos_Thailand_1.png?v=1786599759',
    'https://cdn.shopify.com/s/files/1/0299/5830/7976/files/1_Laz_SBD_3_1.png?v=1786599759',
    'https://cdn.shopify.com/s/files/1/0299/5830/7976/files/IMG_6186.CR2_1.png?v=1786599759',
    'https://cdn.shopify.com/s/files/1/0299/5830/7976/files/2_TC_Wheelock_1.png?v=1786599759',
    'https://cdn.shopify.com/s/files/1/0299/5830/7976/files/1_Laz_SBD_4.png?v=1786599759',
    'https://cdn.shopify.com/s/files/1/0299/5830/7976/files/1_TC_Promoters_1.png?v=1786599759',
    'https://cdn.shopify.com/s/files/1/0299/5830/7976/files/1_TC_Young_1.png?v=1786599759',
    'https://cdn.shopify.com/s/files/1/0299/5830/7976/files/1_Reubs_SRA_1.png?v=1786599759',
    'https://cdn.shopify.com/s/files/1/0299/5830/7976/files/1_GPTW25_1.png?v=1786599759',
    'https://cdn.shopify.com/s/files/1/0299/5830/7976/files/1_Wheelock_Pop_3.png?v=1786599760',
    'https://cdn.shopify.com/s/files/1/0299/5830/7976/files/2_TC_GPTW_1.png?v=1786599759',
    'https://cdn.shopify.com/s/files/1/0299/5830/7976/files/2_TC_Vibes_1.png?v=1786599760'
  ];
  var WALL_ROWS = [ WALL_URLS.slice(0, 9), WALL_URLS.slice(9) ];

  /* ==========================================================
     1 · THE TUNNEL
     ========================================================== */
  var SPAN  = 3400;   // one full depth cycle, in px of z
  var Z_FAR = -3000;  // z of the far plane; near plane is Z_FAR + SPAN
  var CYCLES = 3.2;   // depth cycles travelled across the whole runway
  var DRIFT = SPAN / 70;  // ambient px/sec so it breathes before you scroll

  var runway = document.querySelector('.hero-runway');
  var tunnel = document.querySelector('.tunnel');

  if (tunnel) {
    /* Each tile is its own composited layer. Past ~16 the GPU spends more
       time compositing than drawing and the depth illusion doesn't
       improve — raised to 22/12 on request 2026-08-13 ("feel more full,
       less sparse") anyway, since 16 was reading as sparse on real
       displays rather than as the ceiling this comment describes. Paired
       with a tighter spread (below) rather than count alone, so the field
       packs closer instead of just adding more GPU layers spread just as
       thin as before. */
    var COUNT = window.innerWidth < 760 ? 12 : 22;
    var tiles = [];
    var GOLDEN = 2.39996323;

    /* Deterministic pseudo-random in [0,1). We can't use Math.random
       here — the same tile must resolve to the same place on any given
       pass, or scrolling back up would reshuffle the world. */
    function hash(n) {
      var x = Math.sin(n * 12.9898) * 43758.5453;
      return x - Math.floor(x);
    }

    /* Scatter via golden angle so it never resolves into a grid,
       with a keep-out ellipse in the middle so the headline wins. */
    function scatter(i) {
      var a1 = i * GOLDEN;
      var a2 = i * GOLDEN * 0.618 + 1.05;
      var rr = 0.34 + ((i * 7) % 11) / 11 * 0.66;
      var x = Math.cos(a1) * rr;
      var y = Math.sin(a2) * rr;
      var m = Math.hypot(x, y);
      if (m < 0.46) { var k = 0.46 / (m || 1e-4); x *= k; y *= k; }
      return { x: x, y: y };
    }

    /* Called the instant a tile wraps back to the far plane, where it
       sits at opacity 0. Without this every tile returns to the exact
       same x/y and the tunnel reads as one fixed constellation zooming
       at you, instead of an endless stream of different people. */
    function reseed(tl, cycle) {
      var seed = tl.i * 131 + cycle * 977;
      var a = hash(seed) * Math.PI * 2;
      var r = 0.40 + hash(seed + 99) * 0.60;
      var x = Math.cos(a) * r;
      var y = Math.sin(a + hash(seed + 7) * 1.7) * r;
      var m = Math.hypot(x, y);
      if (m < 0.46) { var k = 0.46 / (m || 1e-4); x *= k; y *= k; }
      tl.nx = x; tl.ny = y;

      // advance through the cast so faces don't repeat pass to pass
      var pi = (((tl.i + cycle * COUNT) % PEOPLE.length) + PEOPLE.length) % PEOPLE.length;
      var person = PEOPLE[pi];
      if (tl.img.getAttribute('src').indexOf(person[0]) === -1) {
        tl.img.src = img(person[0], 400);
      }
      // Tile dimensions are NOT re-randomised here on purpose — changing
      // --w/--h dirties layout, and the whole point of this loop is to
      // stay on the compositor. Size variety comes from the initial seed.
    }

    var frag = document.createDocumentFragment();
    for (var i = 0; i < COUNT; i++) {
      var person = PEOPLE[i % PEOPLE.length];
      var s = scatter(i);
      var wide = i % 3 === 0;
      var w = Math.round((wide ? 300 : 240) * (0.78 + ((i * 5) % 7) / 7 * 0.5));
      var h = Math.round(w * (wide ? 0.72 : 1.26));

      var el = document.createElement('div');
      el.className = 'tile3d';
      el.style.setProperty('--w', w + 'px');
      el.style.setProperty('--h', h + 'px');

      var im = document.createElement('img');
      im.src = img(person[0], 400);
      im.alt = '';
      im.loading = i < 6 ? 'eager' : 'lazy';
      im.decoding = 'async';
      el.appendChild(im);

      frag.appendChild(el);
      tiles.push({
        i: i, el: el, img: im,
        // spread the starting depths evenly through the cycle
        base: (SPAN / COUNT) * i,
        nx: s.x, ny: s.y,
        _op: -1, _cycle: 0
      });
    }
    tunnel.appendChild(frag);

    var vw = window.innerWidth, vh = window.innerHeight;
    var spreadX, spreadY;
    function measure() {
      vw = window.innerWidth; vh = window.innerHeight;
      // Generous spread: perspective pulls far tiles inward, so the
      // pre-projection field has to be wider than the viewport.
      // With reduced motion there's no perspective to pull them back,
      // so the field has to be tightened or half the tiles fall offscreen.
      // Tightened from .95/.95 2026-08-13 (paired with the COUNT bump
      // above) so the extra tiles pack the frame instead of just
      // spreading the same density over more layers.
      spreadX = vw * (reduce ? 0.40 : 0.80);
      spreadY = vh * (reduce ? 0.38 : 0.78);
    }
    measure();

    /* Opacity envelope over normalised depth 0..1.
       There is deliberately NO blur term. An animated `filter: blur()`
       re-rasterises the whole layer every time the radius changes —
       across 20-odd large tiles that alone drops a real display to
       single-digit fps. The long fade-in from the far plane plus the
       hero scrim already reads as atmospheric haze. */
    function envelope(t) {
      var op;
      if (t < 0.02)      { op = 0; }
      else if (t < 0.24) { op = (t - 0.02) / 0.22; }   // slow emerge = haze
      else if (t < 0.76) { op = 1; }
      else if (t < 0.94) { op = 1 - (t - 0.76) / 0.18; }
      else               { op = 0; }
      return clamp(op, 0, 1);
    }

    var drift = 0, last = 0, running = false, live = true;
    var heroCopy = document.querySelector('.hero-copy');
    var yrNow    = document.querySelector('.yr-now');
    var railFill = document.querySelector('.depth-rail i');
    var cue      = document.querySelector('.scroll-cue');
    var lastYear = -1, lastRail = -1, lastCue = -1, lastFade = -1;

    function progress() {
      if (!runway) return 0;
      var total = runway.offsetHeight - vh;
      if (total <= 0) return 0;
      return clamp(-runway.getBoundingClientRect().top / total, 0, 1);
    }

    function paint(travel, p) {
      for (var j = 0; j < tiles.length; j++) {
        var tl = tiles[j];
        var raw = tl.base + travel;
        var cycle = Math.floor(raw / SPAN);
        if (cycle !== tl._cycle) { tl._cycle = cycle; reseed(tl, cycle); }
        var t = ((raw % SPAN) + SPAN) % SPAN;   // 0 = far, SPAN = past camera
        var z = Z_FAR + t;
        var o = Math.round(envelope(t / SPAN) * 100) / 100;

        // fully faded tiles still cost compositing — take them out entirely
        if (o <= 0) {
          if (tl._op !== 0) { tl.el.style.opacity = '0'; tl.el.style.visibility = 'hidden'; tl._op = 0; }
          continue;
        }
        if (tl._op === 0) tl.el.style.visibility = '';

        tl.el.style.transform =
          'translate3d(' + (tl.nx * spreadX).toFixed(0) + 'px,' +
                           (tl.ny * spreadY).toFixed(0) + 'px,' +
                           z.toFixed(0) + 'px)';

        if (o !== tl._op) { tl.el.style.opacity = o; tl._op = o; }
      }

      // flight instruments — every write below is guarded, so a frame
      // where nothing changed costs nothing
      var year = 1966 + Math.round(p * 60);
      if (year !== lastYear && yrNow) { yrNow.textContent = year; lastYear = year; }

      var rp = Math.round(p * 200) / 200;
      if (railFill && rp !== lastRail) { railFill.style.transform = 'scaleX(' + rp + ')'; lastRail = rp; }

      var cueOn = p <= 0.015 ? 1 : 0;
      if (cue && cueOn !== lastCue) { cue.style.opacity = cueOn; lastCue = cueOn; }

      // hand the stage over to the path chooser at the end of the flight
      if (heroCopy) {
        var f = Math.round(clamp((p - 0.74) / 0.22, 0, 1) * 200) / 200;
        if (f !== lastFade) {
          heroCopy.style.opacity = 1 - f;
          heroCopy.style.transform = f
            ? 'translate3d(0,' + (-f * 46).toFixed(0) + 'px,0) scale(' + (1 - f * 0.05).toFixed(3) + ')'
            : '';
          heroCopy.style.pointerEvents = f > 0.85 ? 'none' : '';
          lastFade = f;
        }
      }
    }

    if (reduce) {
      // static scattered collage — no runway, no loop
      paint(0, 0);
      if (yrNow) yrNow.textContent = '1966';
    } else {
      var loop = function (now) {
        if (!running) return;
        var dt = last ? Math.min((now - last) / 1000, 0.05) : 0;
        last = now;
        drift += dt * DRIFT;
        var p = progress();
        paint(p * SPAN * CYCLES + drift, p);
        requestAnimationFrame(loop);
      };
      var start = function () {
        if (running) return;
        running = true; last = 0;
        requestAnimationFrame(loop);
      };
      var stop = function () { running = false; };

      // park the loop completely once the hero has scrolled away, and
      // keep the shared capture bar off the instrument panel mid-flight
      if ('IntersectionObserver' in window && runway) {
        new IntersectionObserver(function (entries) {
          var inView = entries[0].isIntersecting;
          document.body.classList.toggle('in-flight', inView);
          inView ? start() : stop();
        }, { rootMargin: '10% 0px' }).observe(runway);
      } else {
        start();
      }

      window.addEventListener('resize', measure, { passive: true });
    }
  }

  /* ==========================================================
     2 · ROLLING WORDS
     Active word rests at y:0; words already shown exit upward,
     upcoming words wait below. Container width animates to the
     active word so the centred line never jumps.
     ========================================================== */
  var roller = document.querySelector('[data-roller]');
  if (roller) {
    var words = Array.prototype.slice.call(roller.querySelectorAll('.roller-word'));
    var widths = [];
    var idx = 0;

    /* The words are pinned left:0/right:0, so their own rect is the
       roller's width, not the text's — measure through a probe that
       sizes to content instead. */
    var probe = document.createElement('span');
    probe.className = 'roller-word roller-measure';
    probe.setAttribute('aria-hidden', 'true');
    roller.appendChild(probe);

    var measureWords = function () {
      // Italic display faces overhang their advance width, so pad both
      // sides or `overflow:hidden` shaves the tail off.
      //
      // 2026-08-03: the roller went back INLINE, so the box is no longer
      // frozen at the widest word. It was sized that way because a
      // fixed-width slot leaves visible dead space after a short word like
      // "Staff" when something sits beside it — true, and the reason it was
      // moved onto its own centred line in the first place. Now that the
      // sub-header has to be one line, the original reference's answer is
      // the only one available: animate the box to each word's own width.
      // Every word was already being measured here to find the maximum, so
      // this costs nothing but the assignment.
      //
      // --rw-max stays as the CSS fallback for the frame before the first
      // measurement lands, so the line never paints collapsed.
      var pad = parseFloat(getComputedStyle(roller).fontSize) * 0.22;
      widths = words.map(function (w) {
        probe.textContent = w.textContent;
        return Math.ceil(probe.getBoundingClientRect().width + pad);
      });
      probe.textContent = '';
      roller.style.setProperty('--rw-max', Math.max.apply(null, widths) + 'px');
      setWidth(idx);
    };

    // Kept separate from show() because measureWords() has to be able to
    // re-apply the current width on resize without re-running the
    // active/past class flip.
    //
    // ANCHORED on request 2026-08-13 ("standardise it so the line doesn't
    // keep jumping left and right... anchored"): this used to set `--rw`
    // to the ACTIVE word's own width (widths[k]), animating the clip box
    // to match each word — that was itself a fix for a different problem
    // (see the "one line" note on `.hero-roll .roller` in tc60-v6.css §2),
    // but it means the line's centre/edges shift on every rotation. Every
    // word now sits in the same widest-word slot instead, so the line's
    // width never changes — only the word inside it does. `widths` is
    // still measured per word (for --rw-max, the widest), just no longer
    // applied per word.
    var setWidth = function () {
      var w = widths.length ? Math.max.apply(null, widths) : 0;
      if (w) roller.style.setProperty('--rw', w + 'px');
    };

    var show = function (k) {
      for (var m = 0; m < words.length; m++) {
        words[m].classList.toggle('is-active', m === k);
        words[m].classList.toggle('is-past', m < k);
      }
      setWidth(k);
    };

    show(0);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measureWords);
    } else {
      window.addEventListener('load', measureWords);
    }
    measureWords();
    window.addEventListener('resize', measureWords, { passive: true });

    if (!reduce) {
      /* 1300ms hold against a 480ms travel: the word is at rest for most
         of its turn, but the line never feels like it's waiting. */
      setInterval(function () {
        idx = (idx + 1) % words.length;
        show(idx);
      }, 1300);
    }
  }

  /* ==========================================================
     3 · SPOTLIGHT PATH CARDS
     ========================================================== */
  var fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  if (fine && !reduce) {
    document.querySelectorAll('.pathx').forEach(function (card) {
      var queued = false, mx = 0, my = 0, rx = 0, ry = 0;

      var write = function () {
        queued = false;
        card.style.setProperty('--mx', mx.toFixed(2) + '%');
        card.style.setProperty('--my', my.toFixed(2) + '%');
        card.style.setProperty('--rx', rx.toFixed(2) + 'deg');
        card.style.setProperty('--ry', ry.toFixed(2) + 'deg');
      };

      card.addEventListener('pointermove', function (e) {
        var r = card.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width;
        var py = (e.clientY - r.top) / r.height;
        mx = px * 100; my = py * 100;
        rx = -(py - 0.5) * 5;   // restraint: 5deg is plenty
        ry =  (px - 0.5) * 5;
        if (!queued) { queued = true; requestAnimationFrame(write); }
      });

      card.addEventListener('pointerenter', function () { card.classList.add('is-lit'); });
      card.addEventListener('pointerleave', function () {
        card.classList.remove('is-lit');
        card.style.setProperty('--rx', '0deg');
        card.style.setProperty('--ry', '0deg');
        card.style.setProperty('--lift', '0px');
      });
    });
  }

  /* ==========================================================
     4 · HORIZONTAL TIMELINE
     No cards, no 3D. The only scroll-driven value left is a gentle
     opacity falloff away from the middle of the viewport, so the
     milestone you're reading is the loudest thing on the row.
     One property, one write per item — nothing here can jank.
     ========================================================== */
  var rail = document.querySelector('.tl');
  if (rail) {
    var cards = Array.prototype.slice.call(rail.querySelectorAll('.tl-item'));
    var pending = false;

    function drawRail() {
      pending = false;
      var rr = rail.getBoundingClientRect();
      var cx = rr.left + rr.width / 2;
      var half = rr.width / 2 || 1;

      // READ everything first...
      var ds = cards.map(function (c) {
        var b = c.getBoundingClientRect();
        return (b.left + b.width / 2 - cx) / half;
      });

      // ...then WRITE. Never interleave, or every item costs a reflow.
      for (var k = 0; k < cards.length; k++) {
        var op = Math.max(0.42, 1 - Math.abs(ds[k]) * 0.62);
        // quantised so a frame that didn't really change writes nothing
        op = Math.round(op * 50) / 50;
        if (cards[k]._op !== op) { cards[k].style.opacity = op; cards[k]._op = op; }
      }
    }

    var queueRail = function () {
      if (!pending) { pending = true; requestAnimationFrame(drawRail); }
    };

    if (reduce) {
      cards.forEach(function (c) { c.style.opacity = ''; });
    } else {
      rail.addEventListener('scroll', queueRail, { passive: true });
      window.addEventListener('resize', queueRail, { passive: true });
      window.addEventListener('load', queueRail);
      queueRail();

      /* drag to pan */
      var down = false, startX = 0, startScroll = 0, moved = 0;
      rail.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'touch') return;  // let native touch scrolling win
        down = true; moved = 0;
        startX = e.clientX; startScroll = rail.scrollLeft;
        rail.classList.add('is-dragging');
      });
      window.addEventListener('pointermove', function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        moved = Math.max(moved, Math.abs(dx));
        rail.scrollLeft = startScroll - dx;
      });
      window.addEventListener('pointerup', function () {
        if (!down) return;
        down = false;
        rail.classList.remove('is-dragging');
      });
      // suppress the click that follows a drag
      rail.addEventListener('click', function (e) {
        if (moved > 6) { e.preventDefault(); e.stopPropagation(); }
      }, true);
    }

    /* prev / next — the frames are deliberately different widths, so a
       fixed "one card + gap" step would drift. Centre the neighbour of
       whatever is currently nearest the middle instead. */
    var step = function (dir) {
      if (!cards.length) return;
      var rr = rail.getBoundingClientRect();
      var cx = rr.left + rr.width / 2;
      var best = 0, bd = Infinity;
      cards.forEach(function (c, i) {
        var b = c.getBoundingClientRect();
        var d = Math.abs(b.left + b.width / 2 - cx);
        if (d < bd) { bd = d; best = i; }
      });
      var t = cards[clamp(best + dir, 0, cards.length - 1)];
      var tb = t.getBoundingClientRect();
      rail.scrollBy({ left: (tb.left + tb.width / 2) - cx, behavior: reduce ? 'auto' : 'smooth' });
    };
    var prev = document.querySelector('[data-rail-prev]');
    var next = document.querySelector('[data-rail-next]');
    if (prev) prev.addEventListener('click', function () { step(-1); });
    if (next) next.addEventListener('click', function () { step(1); });

    /* keyboard */
    rail.setAttribute('tabindex', '0');
    rail.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
      if (e.key === 'ArrowLeft')  { e.preventDefault(); step(-1); }
    });
  }

  /* ==========================================================
     5 · resolve the timeline photography (data-img -> real src) so the
     markup stays readable and the whole page pulls from one image helper
     ========================================================== */
  document.querySelectorAll('img[data-img]').forEach(function (im) {
    /* data-w lets a caller ask for a smaller render than the 640 default —
       the bundle kit thumbnails are 56px boxes and have no business pulling
       a 640px file six times over.

       data-q REPLACES the whole query string, for the files that need a
       Shopify crop rather than just a width. Added 2026-08-03 for the Arc
       Ultra kit thumb: every Arc Ultra image in the library has marketing
       copy burned into it, and the only clean product shot is a narrow
       centre band of `Webstore_black.jpg`.
       THE TRAP, if you write another one of these: Shopify silently drops
       `crop` when `width` exceeds the SOURCE width, and returns the full
       uncropped image at HTTP 200 with the text still in it. Those sources
       are 1000px, so stay under it. */
    var q = im.getAttribute('data-q');
    im.src = q
      ? CDN + im.getAttribute('data-img') + q
      : img(im.getAttribute('data-img'), +im.getAttribute('data-w') || 640);
  });

  /* ==========================================================
     5b · PROOF & RECOGNITION — the spin carousel
     rotateY per plaque is written here rather than in CSS so the ring
     re-spaces itself if the number of awards changes. The animation
     itself is one CSS keyframe on the parent: N plaques cost the same
     as one, because only the ring transform changes per frame.
     ========================================================== */
  var spinner = document.querySelector('[data-spinner]');
  if (spinner) {
    var plaques = Array.prototype.slice.call(spinner.querySelectorAll('.plaque'));
    plaques.forEach(function (p, i) {

      /* Double-side it. Each plaque authored in the HTML holds ONE block of
         content; here that content is wrapped in a front face and cloned
         into a back face turned 180°, each hiding its own backface. Without
         this, the far half of the ring shows every award logo mirrored —
         see the long note above .plaque in tc60-v3.css. Cloning at init
         rather than authoring both faces by hand keeps the award list a
         single source of truth, and costs nothing per frame: the faces are
         static, only the ring rotates. */
      var front = document.createElement('div');
      front.className = 'p-face';
      while (p.firstChild) front.appendChild(p.firstChild);
      var back = front.cloneNode(true);
      back.className = 'p-face is-back';
      /* the clone duplicates content, so keep it out of the a11y tree and
         out of the (already aria-hidden) reading order twice over */
      back.setAttribute('aria-hidden', 'true');
      p.appendChild(front);
      p.appendChild(back);
    });

    /* ---- how many plaques are on the ring, and where -------------------
       The ring is authored with twelve plaques, six of them [data-dupe]
       second impressions of the two staff awards. Twelve only works where
       there is room for R=660 (a 1358px painted span), so below 1081px the
       duplicates leave the ring entirely and it goes back to the six-plaque
       geometry the narrow tiers were tuned for.

       They have to be REMOVED from the spacing, not just hidden: angles are
       assigned by index over the active set, so a display:none plaque still
       holding its slot would leave a 30° hole in the ring.

       Re-spacing on the breakpoint (not on every resize) keeps this to one
       pass per crossing — and it must re-run, because a laptop dragged onto
       an external display crosses 1081 without a reload. */
    var WIDE = window.matchMedia('(min-width: 1081px)');
    var dupes = plaques.filter(function (p) { return p.hasAttribute('data-dupe'); });

    function space() {
      var wide = WIDE.matches;
      dupes.forEach(function (p) { p.hidden = !wide; });
      var live = wide ? plaques : plaques.filter(function (p) { return !p.hidden; });
      var n = live.length || 1;
      live.forEach(function (p, i) {
        p.style.setProperty('--a', (360 / n * i).toFixed(3) + 'deg');
      });

      /* Guarantee no overlap whatever N ends up being: the chord between
         neighbouring plaques is 2·R·sin(π/N), and it has to clear the card
         width plus a gap. Widen the radius if the count demands it.

         The gap is 6px at twelve and 46px at six. Forty-six was set when the
         ring held six cards and the chord was the only constraint; at twelve
         it would demand R=1174 and a span twice the viewport. What actually
         governs twelve is the projected overlap between a card and the one
         BEHIND it, which the chord test cannot see at all — that trade was
         swept in _ringsweep.mjs and is baked into the CSS radii. So this
         stays what it always was: a floor that stops neighbours interpene-
         trating in the ring plane, not the thing that chooses the radius. */
      /* Cleared BEFORE reading, in both senses: an inline --radius written on
         a previous pass outranks the media query, so leaving it in place
         would make this read its own last answer instead of the tier's. */
      spinner.style.removeProperty('--radius');
      var cs = getComputedStyle(spinner);
      var cw = parseFloat(cs.getPropertyValue('--card-w')) || 250;
      var r  = parseFloat(cs.getPropertyValue('--radius')) || 340;
      var minR = (cw + (n > 8 ? 6 : 46)) / (2 * Math.sin(Math.PI / n));
      if (minR > r) spinner.style.setProperty('--radius', Math.ceil(minR) + 'px');
    }

    space();
    /* addListener is the deprecated spelling; Safari < 14 has only that one */
    if (WIDE.addEventListener) WIDE.addEventListener('change', space);
    else if (WIDE.addListener) WIDE.addListener(space);

    /* --- drag to spin ---------------------------------------------
       The rotation is written to a CSS custom property on .spin-drag, a
       layer OUTSIDE the ring. The auto-spin is a keyframe animating
       .spin-ring's transform, so writing rotateY on that same element
       would be clobbered on every frame of the animation; nesting lets the
       two compose instead — the keyframe keeps ticking underneath and drag
       adds to it. Release hands the last measured velocity to a decay loop
       so the ring coasts rather than stopping dead.

       The tilt that goes with this (30° → 22°) is swept and tabulated in
       tc60-v3.css above .spin-tilt — it is not a free parameter. */
    var dragEl = spinner.querySelector('.spin-drag');
    if (dragEl && window.PointerEvent) {
      var rot = 0, vel = 0, lastX = 0, lastT = 0, dragging = false, glideId = 0, pid = null;
      var SENS = 0.36;        // degrees of ring per px of pointer travel

      var writeRot = function () {
        dragEl.style.setProperty('--drag', rot.toFixed(2) + 'deg');
      };

      var glide = function () {
        vel *= 0.93;
        if (dragging || Math.abs(vel) < 0.03) { vel = 0; glideId = 0; return; }
        rot += vel;
        writeRot();
        glideId = requestAnimationFrame(glide);
      };

      /* Belt to the CSS user-select braces: a pointerdown that isn't
         default-prevented still starts a native text selection / image drag
         the moment the pointer moves, which is what put selection
         highlights and a ghost image on the awards mid-spin. */
      dragEl.addEventListener('dragstart', function (e) { e.preventDefault(); });

      dragEl.addEventListener('pointerdown', function (e) {
        if (e.button) return;                       // primary button / touch only
        e.preventDefault();
        dragging = true; pid = e.pointerId;
        lastX = e.clientX; lastT = e.timeStamp; vel = 0;
        if (glideId) { cancelAnimationFrame(glideId); glideId = 0; }
        spinner.classList.add('is-dragging');
        try { dragEl.setPointerCapture(pid); } catch (err) { /* not fatal */ }
      });

      dragEl.addEventListener('pointermove', function (e) {
        if (!dragging || e.pointerId !== pid) return;
        var dx = e.clientX - lastX;
        /* normalise to per-frame so the coast speed doesn't depend on how
           often the device reports moves */
        var dt = Math.max(e.timeStamp - lastT, 8);
        rot += dx * SENS;
        vel = dx * SENS * (16.7 / dt);
        lastX = e.clientX; lastT = e.timeStamp;
        writeRot();
      });

      var endDrag = function () {
        if (!dragging) return;
        dragging = false;
        spinner.classList.remove('is-dragging');
        if (pid !== null) {
          try { dragEl.releasePointerCapture(pid); } catch (err) { /* already gone */ }
        }
        pid = null;
        // don't coast under reduced motion: the drag itself is user-driven,
        // the momentum afterwards is not
        if (reduce) { vel = 0; return; }
        vel = clamp(vel, -13, 13);
        if (!glideId) glideId = requestAnimationFrame(glide);
      };
      dragEl.addEventListener('pointerup', endDrag);
      dragEl.addEventListener('pointercancel', endDrag);
      dragEl.addEventListener('lostpointercapture', endDrag);
    }
  }

  /* ==========================================================
     5c · THE PEOPLE — the wall, now a continuous auto-scrolling photo
     marquee. Replaced 2026-08-13: this used to be two rows whose position
     was a function of how far the section had scrolled, each tile with a
     hover-reveal name/role/quote card. Both are gone on request — no more
     captions (the new photo set has none to show), and no more hover
     effect on the photos at all. The two rows now drift continuously on
     their own, in opposite directions, independent of scroll.

     Each row's DOM is the row's own nine photos repeated COPIES times
     back to back. That repetition is what makes the loop invisible: since
     every copy is pixel-identical, sliding the row by exactly one copy's
     width (`setW`) and snapping the offset back by that same width lands
     on a frame indistinguishable from the one before the snap. COPIES=4
     keeps at least 3 copies' worth of runway on screen at any one time,
     which comfortably outruns even an ultra-wide viewport.
     ========================================================== */
  var wall = document.querySelector('[data-wall]');
  if (wall) {
    var DIRS = [-1, 1];        // row 1 drifts left, row 2 drifts right
    var SPEED = 46;             // px/sec — bumped from 26 on request 2026-08-13
    var COPIES = reduce ? 1 : 4;
    var rows = [];

    /* RATIO PRELOAD — added 2026-08-14, fixing the "resets after moving
       for a millisecond" glitch reported once tiles stopped cropping
       (tc60-v3.css's `.wt`/`.wt img` went from a fixed `--tile-w` to
       `width: auto; height: 100%`, so the full photo shows instead of a
       cropped square).

       ROOT CAUSE: with `width: auto`, a tile's rendered width depends on
       its `<img>`'s aspect ratio — but `loading="lazy"` (every tile past
       the first four) means the browser doesn't even fetch the photo
       until it's near-viewport, so at the ONE synchronous `measure()`
       call below, most images hadn't loaded yet and measured at ~0px
       wide. `setW` came out ~0, and the wraparound check
       (`offset <= -setW`) then true almost the instant a row moved at
       all — the row would drift a frame or two, hit that near-zero
       boundary, and snap back, over and over. The old fixed-width tiles
       never hit this because their width came from CSS, not from a
       photo that had to finish loading first.

       FIX: learn every photo's aspect ratio through a detached `Image()`
       probe (which fetches regardless of the real `<img>`'s `loading`
       attribute — that attribute only defers DOM-visible images), then
       bake it into the real `<img>`'s `width`/`height` attributes before
       it's ever appended. Per the HTML/CSS spec, a sized `<img>` gets a
       UA-default `aspect-ratio` from those attributes, so the browser
       can lay out the correct rendered width from the ratio ALONE —
       before the pixels themselves have downloaded or decoded. `measure()`
       therefore sees real widths on its first (and only) call, same as
       the fixed-width version did. The wall isn't built until every
       photo's ratio is known (only 18 unique URLs, so this costs
       milliseconds, and the section is below the fold regardless). */
    var uniqueSrcs = [];
    WALL_ROWS.forEach(function (urls) {
      urls.forEach(function (u) { if (uniqueSrcs.indexOf(u) === -1) uniqueSrcs.push(u); });
    });
    var ratios = {};
    var pending = uniqueSrcs.length;

    function wallCell(src) {
      var t = document.createElement('div');
      t.className = 'wt';
      t.setAttribute('aria-hidden', 'true');
      var im = document.createElement('img');
      im.alt = '';
      im.loading = 'lazy';
      im.decoding = 'async';
      if (ratios[src]) {
        // Only the RATIO matters — CSS (`width:auto; height:100%`) still
        // decides the actual rendered size. 1000 is an arbitrary base.
        im.width = Math.round(ratios[src] * 1000);
        im.height = 1000;
      }
      im.src = src;
      t.appendChild(im);
      return t;
    }

    function buildWall() {
      WALL_ROWS.forEach(function (urls, ri) {
        var row = document.createElement('div');
        row.className = 'wall-row';
        var copies = [];
        for (var c = 0; c < COPIES; c++) copies = copies.concat(urls);
        copies.forEach(function (src, i) {
          var cell = wallCell(src);
          // only the tiles that are certainly above the fold load eagerly
          if (ri === 0 && i < 4) cell.querySelector('img').loading = 'eager';
          row.appendChild(cell);
        });
        wall.appendChild(row);
        rows.push({ el: row, dir: DIRS[ri % DIRS.length], n: urls.length,
                    offset: 0, setW: 0 });
      });

      /* setW = the pixel width of exactly one un-repeated copy of the row's
         photos — measured, not computed from a CSS var, so it's correct
         whatever --tile-w resolves to at the current breakpoint. Reliable
         now that every tile's width/height attributes are set before it's
         appended (see the RATIO PRELOAD note above) — this used to be the
         line that measured ~0px for any image that hadn't loaded yet. */
      function measure() {
        rows.forEach(function (r) {
          var w = 0;
          for (var i = 0; i < r.n; i++) {
            var rect = r.el.children[i].getBoundingClientRect();
            w += rect.width;
          }
          var gap = parseFloat(getComputedStyle(r.el).gap) || 0;
          r.setW = w + gap * r.n;
          r.offset = r.dir < 0 ? 0 : -r.setW;
          r.el.style.transform = 'translate3d(' + r.offset.toFixed(2) + 'px,0,0)';
        });
      }
      measure();
      window.addEventListener('resize', measure, { passive: true });

      startWallLoop(rows);
    }

    if (pending === 0) {
      buildWall();
    } else {
      uniqueSrcs.forEach(function (src) {
        var probe = new Image();
        probe.onload = probe.onerror = function () {
          if (probe.naturalWidth && probe.naturalHeight) {
            ratios[src] = probe.naturalWidth / probe.naturalHeight;
          }
          pending--;
          if (pending === 0) buildWall();
        };
        probe.src = src;
      });
    }
  }

  function startWallLoop(rows) {
    if (!reduce) {
      var wallVisible = false, last = null;

      var step = function (now) {
        if (!wallVisible) { last = null; return; }
        if (last !== null) {
          var dt = Math.min(now - last, 100) / 1000;
          rows.forEach(function (r) {
            r.offset += r.dir * SPEED * dt;
            if (r.dir < 0 && r.offset <= -r.setW) r.offset += r.setW;
            if (r.dir > 0 && r.offset >= 0) r.offset -= r.setW;
            r.el.style.transform = 'translate3d(' + r.offset.toFixed(2) + 'px,0,0)';
          });
        }
        last = now;
        requestAnimationFrame(step);
      };

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (es) {
          wallVisible = es[0].isIntersecting;
          if (wallVisible) { last = null; requestAnimationFrame(step); }
        }, { rootMargin: '120px 0px' }).observe(wall);
      } else {
        wallVisible = true;
        requestAnimationFrame(step);
      }
    }
  }

  /* 5d · the pointer-tracked specular highlight that used to live here
     went out with the brushed-metal button surface. Buttons are flat now,
     so there is nothing per-frame to write. */

  /* ==========================================================
     6 · topbar: transparent through the flight, solid after.
     Sentinel sits at the foot of the chooser, which is the last
     dark section before the page turns light.
     ========================================================== */
  var topbar = document.querySelector('.topbar-v3');
  var darkEnd = document.querySelector('[data-dark-end]');
  if (topbar && darkEnd) {
    // `!isIntersecting` is true both ABOVE and BELOW the viewport, so it
    // can't distinguish "not there yet" from "scrolled past". Read the
    // sentinel's actual position instead: solid only once it's behind
    // the bar.
    var syncBar = function () {
      topbar.classList.toggle('is-solid', darkEnd.getBoundingClientRect().top <= 68);
    };
    window.addEventListener('scroll', syncBar, { passive: true });
    window.addEventListener('resize', syncBar, { passive: true });
    syncBar();
  }

  /* ==========================================================
     7 · smooth "skip ahead" out of the flight
     ========================================================== */
  document.querySelectorAll('[data-skip]').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      var target = document.querySelector(btn.getAttribute('data-skip'));
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    });
  });
})();
